import {
  startOfDay,
  endOfDay,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  subDays,
  addDays,
} from "date-fns";

export type CalendarView = "month" | "week" | "day" | "agenda";

export interface CalendarRange {
  from: string;
  to: string;
}

/**
 * Given a react-big-calendar view name and the current navigation date,
 * returns the [from, to] window that should be fetched from the API.
 * Uses weekStartsOn: 1 (Monday) to match European conventions.
 * Adds a 1-day buffer on each side for month view to capture events that
 * start in the previous/next month but appear in the calendar grid.
 */
export function getCalendarRange(view: CalendarView, date: Date): CalendarRange {
  let start: Date;
  let end: Date;

  switch (view) {
    case "day":
      start = startOfDay(date);
      end = endOfDay(date);
      break;
    case "week":
      start = startOfWeek(date, { weekStartsOn: 1 });
      end = endOfWeek(date, { weekStartsOn: 1 });
      break;
    case "agenda":
      // Agenda view: show the next 30 days from the navigation date
      start = startOfDay(date);
      end = addDays(startOfDay(date), 30);
      break;
    case "month":
    default:
      // Month view grid can show up to 6 weeks; fetch the whole grid with buffer
      start = subDays(startOfWeek(startOfMonth(date), { weekStartsOn: 1 }), 1);
      end = addDays(endOfWeek(endOfMonth(date), { weekStartsOn: 1 }), 1);
      break;
  }

  return {
    from: start.toISOString(),
    to: end.toISOString(),
  };
}

/**
 * Parse a YYYY-MM-DD string (from URL param) into a Date.
 * Falls back to today if the string is invalid.
 */
export function parseDateParam(value: string | null): Date {
  if (!value) return new Date();
  const d = new Date(value + "T00:00:00");
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

/**
 * Format a Date to YYYY-MM-DD for use as a URL param.
 */
export function formatDateParam(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}
